"use client";

import { useCallback, useEffect, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import {
  isPushSupported,
  getPlatform,
  checkPermissions,
  registerForPush,
  unregisterFromPush,
  onNotificationReceived,
  onNotificationTapped,
  clearBadge,
} from "@/lib/push-notifications";
import { App } from "@capacitor/app";
import { useNavigate } from "react-router";

export function usePushNotifications() {
  const navigate = useNavigate();
  const [isSupported] = useState(() => isPushSupported());
  const [permissionStatus, setPermissionStatus] = useState<"prompt" | "granted" | "denied">("prompt");
  const [isRegistering, setIsRegistering] = useState(false);
  const [currentToken, setCurrentToken] = useState<string | null>(null);

  const tokens = useQuery(api.pushNotifications.getMyTokens);
  const registerTokenMutation = useMutation(api.pushNotifications.registerToken);
  const removeTokenMutation = useMutation(api.pushNotifications.removeToken);

  // Check permission status on mount
  useEffect(() => {
    if (isSupported) {
      checkPermissions().then(setPermissionStatus);
    }
  }, [isSupported]);

  // Clear badge when app becomes active
  useEffect(() => {
    if (!isSupported) return;

    // Clear badge on initial mount (app open)
    clearBadge();

    // Clear badge when app returns to foreground
    const listener = App.addListener("appStateChange", ({ isActive }) => {
      if (isActive) {
        clearBadge();
      }
    });

    return () => {
      listener.then((l) => l.remove());
    };
  }, [isSupported]);

  // Set up notification listeners
  useEffect(() => {
    if (!isSupported) return;

    // Handle foreground notifications
    const unsubReceived = onNotificationReceived((notification) => {
      console.log("Notification received in foreground:", notification);
      // Could show a toast or in-app notification here
    });

    // Handle notification taps
    const unsubTapped = onNotificationTapped((notification) => {
      console.log("Notification tapped:", notification);

      // Clear badge when notification is tapped
      clearBadge();

      // Navigate based on notification type
      if (notification.data?.type === "block_reminder" && notification.data?.blockId) {
        navigate(`/blocks/${notification.data.blockId}`);
      } else if (notification.data?.type === "support_email") {
        navigate("/email");
      } else if (notification.data?.type === "admin" && notification.data?.deepLink) {
        // Admin notifications with deep links
        navigate(notification.data.deepLink as string);
      }
    });

    return () => {
      unsubReceived();
      unsubTapped();
    };
  }, [isSupported, navigate]);

  // Register for push notifications and save token to Convex
  const register = useCallback(async (): Promise<boolean> => {
    console.log("[Push] register() called, isSupported:", isSupported);
    if (!isSupported) {
      console.log("[Push] Push notifications not supported");
      return false;
    }

    const platform = getPlatform();
    console.log("[Push] Platform:", platform);
    if (!platform) {
      console.log("[Push] Unknown platform");
      return false;
    }

    setIsRegistering(true);

    try {
      console.log("[Push] Calling registerForPush()...");
      const token = await registerForPush();
      console.log("[Push] registerForPush returned token:", token ? token.substring(0, 20) + "..." : "null");

      if (token) {
        // Save token to Convex
        console.log("[Push] Saving token to Convex for platform:", platform);
        await registerTokenMutation({ token, platform });
        console.log("[Push] Token saved to Convex successfully");
        setCurrentToken(token);
        setPermissionStatus("granted");
        return true;
      }

      console.log("[Push] No token received, setting denied");
      setPermissionStatus("denied");
      return false;
    } catch (error) {
      console.error("[Push] Failed to register:", error);
      return false;
    } finally {
      setIsRegistering(false);
    }
  }, [isSupported, registerTokenMutation]);

  // Unregister from push notifications
  const unregister = useCallback(async (): Promise<void> => {
    if (currentToken) {
      await removeTokenMutation({ token: currentToken });
    }

    await unregisterFromPush();
    setCurrentToken(null);
  }, [currentToken, removeTokenMutation]);

  return {
    isSupported,
    isEnabled: tokens && tokens.length > 0,
    isRegistering,
    permissionStatus,
    tokens,
    register,
    unregister,
  };
}
